import { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../lib/translations';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import LeadForm from '../LeadForm';
import {
  Download, Save, RotateCcw, ChevronLeft, BookOpen, Users,
  ShieldCheck, Settings, Plus, CheckCircle2, FileCheck, Mail, Globe, Share2, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_420a1fc0-c22b-4adc-97e3-0401a469efb3/artifacts/nomkxozs_Kurdynowski%20%282%29.png';

export default function Step5Results({ formData, totals, onReset, onBack }) {
  const { language } = useLanguage();
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const ccy = t('common.currency', language);
  const accountingLabel = t(`step1.${formData.accountingForm}`, language);
  const entityLabel = t(`step1.entities.${formData.legalEntity}`, language);

  const breakdownItems = [
    { icon: BookOpen, label: t('step5.basePrice', language), desc: accountingLabel, amount: totals.basePrice },
    totals.vatCost > 0 && {
      icon: FileCheck, label: t('step5.vatCost', language),
      desc: language === 'pl' ? 'Deklaracja VAT-7' : 'VAT-7 declaration',
      amount: totals.vatCost,
    },
    totals.vatEuCost > 0 && {
      icon: Globe, label: t('step5.vatEuCost', language),
      desc: language === 'pl' ? 'Deklaracja VAT EU' : 'VAT EU declaration',
      amount: totals.vatEuCost,
    },
    totals.employeeCost > 0 && {
      icon: Users, label: t('step5.employees', language),
      desc: `${formData.employeeCount} × 100 ${ccy}`, amount: totals.employeeCost,
    },
    totals.zusCost > 0 && {
      icon: ShieldCheck, label: t('step5.zus', language), desc: 'ZUS', amount: totals.zusCost,
    },
    totals.specificsAdjustment !== 0 && {
      icon: Settings, label: t('step5.specificsAdj', language), desc: '', amount: totals.specificsAdjustment,
    },
    totals.additionalServicesTotal > 0 && {
      icon: Plus, label: t('step5.additionalSvc', language), desc: '', amount: totals.additionalServicesTotal,
    },
  ].filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/quotes`, {
        accounting_form: formData.accountingForm,
        legal_entity: formData.legalEntity,
        vat_payer: formData.vatPayer,
        vat_eu: formData.vatEu,
        document_count: formData.documentCount,
        employee_count: formData.employeeCount,
        zus_owner: formData.zusOwner,
        partner_count: formData.partnerCount,
        zus_persons_count: formData.zusPersonsCount,
        business_specifics: formData.businessSpecifics,
        additional_services: formData.additionalServices,
        base_price: totals.basePrice,
        vat_cost: totals.vatCost,
        vat_eu_cost: totals.vatEuCost,
        employee_cost: totals.employeeCost,
        zus_cost: totals.zusCost,
        specifics_adjustment: totals.specificsAdjustment,
        additional_services_total: totals.additionalServicesTotal,
        monthly_total: totals.monthlyTotal,
        client_name: clientName,
        client_email: clientEmail,
        language,
      });
      toast.success(
        language === 'pl'
          ? `Wycena zapisana! Nr: ${(res?.data?.id || '').slice(0, 8).toUpperCase()}`
          : `Quote saved! Ref: ${(res?.data?.id || '').slice(0, 8).toUpperCase()}`
      );
      setSavedQuoteId(res?.data?.id || '');
    } catch {
      toast.error(t('common.error', language));
    } finally {
      setSaving(false);
    }
  };

  const handlePDF = async () => {
    const doc = new jsPDF();
    const blue = [27, 58, 123];
    const orange = [232, 148, 26];
    const dark = [15, 23, 42];

    // Load Polish-supporting fonts + logo
    let logoLoaded = false;
    try {
      const [regularRes, boldRes, logoRes] = await Promise.all([
        fetch('/fonts/Roboto-Regular.ttf'),
        fetch('/fonts/Roboto-Bold.ttf'),
        fetch('/images/logo.png'),
      ]);
      const [regularBuf, boldBuf, logoBuf] = await Promise.all([
        regularRes.arrayBuffer(),
        boldRes.arrayBuffer(),
        logoRes.arrayBuffer(),
      ]);
      const toBase64 = (buf) => {
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        return btoa(binary);
      };
      doc.addFileToVFS('Roboto-Regular.ttf', toBase64(regularBuf));
      doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
      doc.addFileToVFS('Roboto-Bold.ttf', toBase64(boldBuf));
      doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
      doc.setFont('Roboto', 'normal');

      // Add logo centered (original ratio 558:256 ≈ 2.18:1)
      const logoW = 50;
      const logoH = logoW / 2.18;
      const pageW = doc.internal.pageSize.getWidth();
      const logoX = (pageW - logoW) / 2;
      doc.addImage('data:image/png;base64,' + toBase64(logoBuf), 'PNG', logoX, 8, logoW, logoH);
      logoLoaded = true;
    } catch (e) {
      console.warn('Font/logo load failed, using fallback', e);
    }

    // Header (text fallback if logo didn't load, otherwise skip text logo)
    const headerBottom = logoLoaded ? 34 : 28;
    if (!logoLoaded) {
      doc.setFont('Roboto', 'bold');
      doc.setFontSize(22);
      doc.setTextColor(...blue);
      doc.text('KURDYNOWSKI', 20, 22);
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...orange);
      doc.text('Accounting & Tax Solutions', 20, 28);
    }

    // Line
    doc.setDrawColor(...blue);
    doc.setLineWidth(0.5);
    doc.line(20, headerBottom + 5, 190, headerBottom + 5);

    // Title
    doc.setFont('Roboto', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...dark);
    doc.text(t('step5.pdfTitle', language), 20, headerBottom + 16);

    // Date
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    const dateY = headerBottom + 24;
    doc.text(`${t('step5.dateLabel', language)}: ${new Date().toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB')}`, 20, dateY);

    if (clientName) doc.text(`${t('common.clientName', language)}: ${clientName}`, 20, dateY + 6);

    // Details table
    const detailsStartY = clientName ? dateY + 14 : dateY + 8;
    autoTable(doc, {
      startY: detailsStartY,
      head: [[language === 'pl' ? 'Parametr' : 'Parameter', language === 'pl' ? 'Warto\u015b\u0107' : 'Value']],
      body: [
        [t('step5.accountingLabel', language), accountingLabel],
        [t('step5.entityLabel', language), entityLabel],
        [t('step5.docsLabel', language), String(formData.documentCount)],
        [t('step5.employeesLabel', language), String(formData.employeeCount)],
      ],
      theme: 'grid',
      headStyles: { fillColor: blue, fontSize: 10, font: 'Roboto', fontStyle: 'bold' },
      bodyStyles: { fontSize: 10, font: 'Roboto', fontStyle: 'normal' },
      margin: { left: 20, right: 20 },
    });

    // Price breakdown
    const priceBody = breakdownItems.map(item => [item.label, `${item.amount} ${ccy}`]);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [[language === 'pl' ? 'Sk\u0142adnik' : 'Component', language === 'pl' ? 'Kwota' : 'Amount']],
      body: priceBody,
      foot: [[t('step5.finalTotal', language), `${totals.monthlyTotal} ${ccy}`]],
      theme: 'grid',
      headStyles: { fillColor: blue, fontSize: 10, font: 'Roboto', fontStyle: 'bold' },
      footStyles: { fillColor: orange, textColor: [255, 255, 255], fontSize: 12, fontStyle: 'bold', font: 'Roboto' },
      bodyStyles: { fontSize: 10, font: 'Roboto', fontStyle: 'normal' },
      margin: { left: 20, right: 20 },
    });

    // Footer
    doc.setFont('Roboto', 'normal');
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('Biuro Rachunkowe Kurdynowski', 20, pageHeight - 15);
    doc.text(language === 'pl' ? 'Wycena ma charakter orientacyjny' : 'This quote is an estimate', 20, pageHeight - 10);

    doc.save(`wycena-kurdynowski-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(language === 'pl' ? 'PDF zapisany' : 'PDF saved');
  };

  const handleSendEmail = async () => {
    if (!clientEmail || !clientEmail.includes('@')) {
      toast.error(t('step5.emailRequired', language));
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/send-quote-email`, {
        recipient_email: clientEmail,
        client_name: clientName,
        accounting_form: formData.accountingForm,
        legal_entity: formData.legalEntity,
        document_count: formData.documentCount,
        employee_count: formData.employeeCount,
        vat_payer: formData.vatPayer,
        vat_eu: formData.vatEu,
        base_price: totals.basePrice,
        vat_cost: totals.vatCost,
        vat_eu_cost: totals.vatEuCost,
        employee_cost: totals.employeeCost,
        zus_cost: totals.zusCost,
        specifics_adjustment: totals.specificsAdjustment,
        additional_services_total: totals.additionalServicesTotal,
        monthly_total: totals.monthlyTotal,
        language,
      });
      toast.success(t('step5.emailSent', language));
    } catch {
      toast.error(t('step5.emailError', language));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div data-testid="step-5-results">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B] mb-2 text-center">
        {t('step5.title', language)}
      </h2>
      <p className="text-slate-500 mb-8 text-base text-center">
        {t('step5.subtitle', language)}
      </p>

      {/* Total Price Card */}
      <div className="receipt-card bg-white rounded-xl border border-slate-200 p-8 shadow-xl max-w-2xl mx-auto mb-8">
        <div className="text-center mb-8">
          <div className="font-mono-price text-5xl sm:text-6xl font-bold text-[#1B3A7B] mb-2" data-testid="total-price">
            {totals.monthlyTotal} {ccy}
          </div>
          <p className="text-slate-500 text-sm">{t('common.net', language)} {t('common.monthly', language)}</p>
        </div>

        <Separator className="mb-6" />

        {/* Included */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">
            <CheckCircle2 className="w-4 h-4 inline mr-1.5 text-green-600" />
            {t('step5.included', language)}
          </h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1B3A7B]" />
              {accountingLabel} — {language === 'pl' ? 'do' : 'up to'} {formData.documentCount} {language === 'pl' ? 'pozycji/mies.' : 'entries/mo.'}
            </li>
            {formData.vatPayer && (
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1B3A7B]" />
                {language === 'pl' ? 'Obsługa VAT' : 'VAT service'}
              </li>
            )}
            {formData.employeeCount > 0 && (
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1B3A7B]" />
                {t('step5.employees', language)} — {formData.employeeCount} {t('step3.persons', language)}
              </li>
            )}
            {formData.zusOwner && (
              <li className="flex items-center gap-2 text-sm text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#1B3A7B]" />
                {t('step3.zusOwner', language)}
              </li>
            )}
          </ul>
        </div>

        <Separator className="mb-6" />

        {/* Breakdown */}
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
          {t('step5.breakdown', language)}
        </h3>
        <div className="space-y-3">
          {breakdownItems.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-slate-500" />
                  </div>
                  <div>
                    <span className="text-sm font-medium text-slate-800">{item.label}</span>
                    {item.desc && <span className="text-xs text-slate-500 ml-2">{item.desc}</span>}
                  </div>
                </div>
                <span className={`font-mono-price font-semibold text-sm ${item.amount < 0 ? 'text-green-600' : 'text-slate-800'}`}>
                  {item.amount > 0 ? '+' : ''}{item.amount} {ccy}
                </span>
              </div>
            );
          })}
        </div>

        <Separator className="my-6" />

        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-slate-800">{t('step5.finalTotal', language)}</span>
          <span className="font-mono-price text-2xl font-bold text-[#E8941A]" data-testid="final-total">
            {totals.monthlyTotal} {ccy}
          </span>
        </div>
      </div>

      {/* Client Info + Actions */}
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            placeholder={t('common.clientName', language)}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="h-11 border-slate-300"
            data-testid="client-name-input"
          />
          <Input
            placeholder={t('common.clientEmail', language)}
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            className="h-11 border-slate-300"
            data-testid="client-email-input"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-12 rounded-md font-medium text-sm bg-[#1B3A7B] hover:bg-[#142d63] text-white flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            data-testid="save-quote-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? '...' : t('common.save', language)}
          </button>
          <button
            onClick={handlePDF}
            className="flex-1 h-12 rounded-md font-medium text-sm bg-[#E8941A] hover:bg-[#d68515] text-white flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            data-testid="download-pdf-btn"
          >
            <Download className="w-4 h-4" />
            {t('common.download', language)}
          </button>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="flex-1 h-12 rounded-md font-medium text-sm bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
            data-testid="send-email-btn"
          >
            <Mail className="w-4 h-4" />
            {sendingEmail ? t('step5.sending', language) : t('step5.sendEmail', language)}
          </button>
        </div>

        {/* Share link (shown after saving) */}
        {savedQuoteId && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3 animate-fade-in" data-testid="share-quote-section">
            <Share2 className="w-5 h-5 text-[#1B3A7B] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-500 mb-1">{language === 'pl' ? 'Udostępnij wycenę:' : 'Share quote:'}</p>
              <p className="text-xs font-mono text-[#1B3A7B] truncate" data-testid="share-url">
                {window.location.origin}/quote/{savedQuoteId}
              </p>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/quote/${savedQuoteId}`);
                toast.success(language === 'pl' ? 'Link skopiowany!' : 'Link copied!');
              }}
              className="px-3 py-2 bg-[#1B3A7B] text-white rounded-md text-xs font-medium hover:bg-[#142d63] transition-colors flex items-center gap-1.5 shrink-0"
              data-testid="copy-share-link-btn"
            >
              <Copy className="w-3.5 h-3.5" />
              {language === 'pl' ? 'Kopiuj' : 'Copy'}
            </button>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 h-11 rounded-md font-medium text-sm border-2 border-slate-300 text-slate-600 hover:border-[#1B3A7B] hover:text-[#1B3A7B] flex items-center justify-center gap-2 transition-all"
            data-testid="modify-params-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            {t('step5.modifyParams', language)}
          </button>
          <button
            onClick={onReset}
            className="flex-1 h-11 rounded-md font-medium text-sm border-2 border-slate-300 text-slate-600 hover:border-[#1B3A7B] hover:text-[#1B3A7B] flex items-center justify-center gap-2 transition-all"
            data-testid="new-calc-btn"
          >
            <RotateCcw className="w-4 h-4" />
            {t('common.newCalc', language)}
          </button>
        </div>

        {/* Lead Form CTA */}
        <div className="mt-6">
          <LeadForm formData={formData} totals={totals} quoteId={savedQuoteId} />
        </div>
      </div>
    </div>
  );
}
